import { DbUpdateObject } from 'common/db/api/types';
import { api } from 'common/frontend/api';
import { BulkInventoryItem, InventoryConfiguration, InventoryItem } from 'common/modules/inventory';
import { InventoryStatuses } from 'common/modules/inventory/constants';
import { ById, Location, PurchaseTypes } from 'common/types';
import { DotNotation } from 'common/types/dotObject';
import { createKeyMap } from 'common/utils/arrays';
import { notUndefined } from 'common/utils/common';
import { newFirestoreId } from 'common/utils/dataMigrations';
import { getTypedKeys } from 'common/utils/objects';
import { chunk, isEmpty, isEqual, pick, uniq, uniqWith } from 'lodash';
import { parse } from 'papaparse';
import type { TFunction } from 'react-i18next';
import {
	FixedArticleColumnField,
	FixedArticleColumns,
} from '../ArticlesView/ArticlesTable/columns';
import { saveNewInventoryFieldsToDb } from '../utils';
import { ColumnImportData, SkuImport, ArticleImport, ExistingInventoryData } from './parse';
import { ImportData } from './VerifyImport';

export const inventoryStatusList = getTypedKeys(InventoryStatuses);
export const inventoryAllocationList = getTypedKeys(PurchaseTypes);

export const allowedImportFields: Extract<
	FixedArticleColumnField,
	| 'id'
	| 'article_code'
	| 'sku_name'
	| 'sku_code'
	| 'store'
	| 'status'
	| 'allocations'
	| 'quantity'
	| 'sole_length'
	| 'din_variation'
	| 'type'
>[] = [
	'id',
	'article_code',
	'sku_name',
	'sku_code',
	'store',
	'status',
	'allocations',
	'quantity',
	'sole_length',
	'din_variation',
	'type',
];

export type AllowedImportField = typeof allowedImportFields[number];

export type SanitizedDataRowObject = { [K in AllowedImportField]?: string } & {
	[customField: string]: string;
};

export type DataRowObject = { [K in FixedArticleColumnField]?: string } & {
	[customField: string]: string;
};

export type CsvUploadData = {
	columns: string[];
	headerRow: DataRowObject;
	dataRows: DataRowObject[];
};

export type CsvParseResult = { data: CsvUploadData };

export const CSV_ID_FIELD: Extract<FixedArticleColumnField, 'id'> = 'id';
export const CSV_ARTICLE_CODE_FIELD: Extract<FixedArticleColumnField, 'article_code'> =
	'article_code';

export const parseCsv = (
	file: File,
	t: TFunction,
	existingHeaders: string[],
): Promise<CsvParseResult> => {
	const generalParsingErrorMessage = t(
		'inventory.import.csvParseError',
		'Error parsing your file. Please make sure the file is in correct format, and has at least one article row.',
	);
	let rowIndex = 0;
	let SECONDARY_HEADER_ROW_INDEX = 1;
	const existingHeadersEnums = existingHeaders.reduce(
		(tot, header) => ({ ...tot, [header]: true as const }),
		{} as { [key: string]: true },
	);
	const nonEmptyHeaderFields: string[] = [];
	let secondaryHeaderRow: DataRowObject = {};
	let dataRows: DataRowObject[] = [];
	return new Promise<CsvParseResult>((resolve, reject) => {
		parse<DataRowObject>(file, {
			header: true,
			skipEmptyLines: true,
			// If a header has empty ID, we fill them with a new ID to make sure that we get all the new columns to the parsed data objects
			transformHeader: (header) =>
				!header || !existingHeadersEnums[header] ? newFirestoreId() : header,
			encoding: 'UTF-8',
			step: (results) => {
				rowIndex++;
				if (rowIndex === SECONDARY_HEADER_ROW_INDEX) {
					Object.entries(results.data).forEach(([key, value]) => {
						if (value === '' || value == null) return;
						if (nonEmptyHeaderFields.includes(key)) return;
						nonEmptyHeaderFields.push(key);
					});
					secondaryHeaderRow = pick(results.data, nonEmptyHeaderFields);
				} else {
					const validRow = pick(results.data, nonEmptyHeaderFields);
					if (Object.values(validRow).every((val) => val === '')) return;
					dataRows.push(validRow);
				}
			},
			complete: async () => {
				const firstHeaderField = nonEmptyHeaderFields?.[0];
				if (firstHeaderField !== CSV_ID_FIELD && firstHeaderField !== CSV_ARTICLE_CODE_FIELD) {
					const incorrectIdError = t('inventory.import.incorrectIdField', {
						defaultValue:
							'Incorrect csv-format: first column must be "{{idField}}" or "{{articleCodeField}}"',
						idField: CSV_ID_FIELD,
						articleCodeField: CSV_ARTICLE_CODE_FIELD,
					});
					return reject(new Error(incorrectIdError));
				}
				if (nonEmptyHeaderFields?.length && !!dataRows.length) {
					return resolve({
						data: {
							columns: nonEmptyHeaderFields,
							headerRow: secondaryHeaderRow,
							dataRows,
						},
					});
				} else {
					return reject(new Error(generalParsingErrorMessage));
				}
			},
			error: async () => {
				return reject(new Error(generalParsingErrorMessage));
			},
		});
	});
};

export const updateInventoryColumns = async (fields: ColumnImportData, shopId: string) => {
	const { toAdd, toUpdate } = fields;
	await saveNewInventoryFieldsToDb(api(), toAdd, shopId);
	const updateObject = Object.entries(toUpdate).reduce((tot, [fieldId, fieldLabel]) => {
		const fieldPath: DotNotation<InventoryConfiguration> = `fields.values.${fieldId}.label` as const;
		return {
			...tot,
			[fieldPath]: fieldLabel,
		};
	}, {} as DbUpdateObject<InventoryConfiguration>);
	return api().inventoryConfigurations.doc(shopId).update(updateObject);
};

export const updateSkus = async (skus: SkuImport) => {
	const { toAdd, toUpdate } = skus;
	await Promise.all(toAdd.map((item) => api().skuItems.doc(item.data.id).set(item.data)));
	await Promise.all(
		toUpdate.map((item) => {
			const { id, ...fieldsToUpdate } = item.data;
			if (isEmpty(fieldsToUpdate)) {
				return undefined;
			}
			return api()
				.skuItems.doc(id)
				.update({ ...fieldsToUpdate });
		}),
	);
	return;
};

export const updateArticles = async (articles: ArticleImport) => {
	const { toAdd, toUpdate } = articles;
	await Promise.all(toAdd.map((item) => api().inventoryItems.doc(item.data.id).set(item.data)));
	await Promise.all(
		toUpdate.map((item) => {
			const { id, ...fieldsToUpdate } = item.data;
			if (isEmpty(fieldsToUpdate)) {
				return undefined;
			}
			// We use { merge: true } because we want to update also nested documents only partially
			return api().inventoryItems.doc(id).set(fieldsToUpdate, { merge: true });
		}),
	);
	return;
};

export const getExistingArticlesByIds = async (
	shopId: string,
	ids: string[],
): Promise<InventoryItem[]> => {
	const fetchExistingArticlesChunk = async (
		shopId: string,
		ids: string[],
	): Promise<InventoryItem[]> => {
		const matchedArticles = await api()
			.inventoryItems.get.where('shopId', '==', shopId)
			.whereIn('id', 'in', ids)
			.get();
		return matchedArticles;
	};
	// Firestore array-contains-any can only take 10 items at a time
	const codeArrayChunks = chunk(ids, 10);
	const existingArticles = (
		await Promise.all(
			codeArrayChunks.map(async (arrayOfTenIds) => {
				return fetchExistingArticlesChunk(shopId, arrayOfTenIds);
			}),
		)
	).flat();
	return existingArticles;
};

export const getExistingArticlesByCodes = async (
	shopId: string,
	codes: string[],
): Promise<InventoryItem[]> => {
	const fetchExistingArticlesChunk = async (
		shopId: string,
		codes: string[],
	): Promise<InventoryItem[]> => {
		const matchedArticles = await api()
			.inventoryItems.get.where('shopId', '==', shopId)
			.where('identifiers', 'array-contains-any', codes)
			.get();
		return matchedArticles;
	};
	// Firestore array-contains-any can only take 10 items at a time
	const codeArrayChunks = chunk(codes, 10);
	const existingArticles = (
		await Promise.all(
			codeArrayChunks.map(async (arrayOfTenCodes) => {
				return fetchExistingArticlesChunk(shopId, arrayOfTenCodes);
			}),
		)
	).flat();
	return existingArticles;
};

export const getExistingBulkArticles = async (
	shopId: string,
	rows: {
		locationId: string;
		skuCode: string;
	}[],
): Promise<BulkInventoryItem[]> => {
	const bulkArticles = await Promise.all(
		rows.map(async ({ locationId, skuCode }) => {
			const bulkArticles = await api()
				.inventoryItems.bulkItems.where('shopId', '==', shopId)
				.where('locationId', '==', locationId)
				.where('skuCode', '==', skuCode)
				.get();
			return bulkArticles;
		}),
	);
	return bulkArticles.flat();
};

export const hasArticleUpdates = (data: ImportData) =>
	!!data.articles.toAdd.length || !!data.articles.toUpdate.length;
export const hasSkuUpdates = (data: ImportData) =>
	!!data.skus.toAdd.length || !!data.skus.toUpdate.length;
export const hasColumnUpdates = (data: ImportData) =>
	!!data.columns.toAdd.length || !!Object.keys(data.columns.toUpdate).length;

export const hasInvalidArticles = (data: ImportData) => !!data.articles.invalid.length;
export const hasInvalidSkus = (data: ImportData) => !!data.skus.invalid.length;
export const hasInvalidColumns = (data: ImportData) => !!Object.keys(data.columns.invalid).length;

export const hasArticleChanges = (data: ImportData) =>
	hasArticleUpdates(data) || hasInvalidArticles(data);
export const hasSkuChanges = (data: ImportData) => hasSkuUpdates(data) || hasInvalidSkus(data);
export const hasColumnChanges = (data: ImportData) =>
	hasColumnUpdates(data) || hasInvalidColumns(data);

export const isValidImport = (data: ImportData) =>
	hasArticleUpdates(data) || hasSkuUpdates(data) || hasColumnUpdates(data) || false;

export const getLocationNameLocationIdMap = (locations: Location[]) => {
	return locations.reduce(
		(tot, loc) => ({
			...tot,
			[loc.name]: loc.id,
		}),
		{} as ById<string>,
	);
};

/**
 *
 * @param locationName Name of the location. `null` equals to no location. If locationName is null, and there is only one location, the id of that location is returned
 * @param locations
 */
export const getLocationIdByRowLocationName = (
	locationName: string | null,
	locations: Location[],
) => {
	if (!locationName && locations.length === 1) return locations[0].id;
	const locationNameLocationIdMap = getLocationNameLocationIdMap(locations);
	return locationName ? locationNameLocationIdMap[locationName] : undefined;
};

const getLocationIdsBySkuCode = (articles: BulkInventoryItem[]) => {
	return articles.reduce((tot, article) => {
		if (!article.skuCode || !article.locationId) return tot;
		return {
			...tot,
			[article.skuCode]: uniq([...(tot[article.skuCode] ?? []), article.locationId]),
		};
	}, {} as { [skuCode: string]: string[] });
};

const getExistingArticlesFromIdField = async (dataRows: DataRowObject[], shopId: string) => {
	const allRowIdCellValues = [
		...new Set(dataRows.map((row) => row[CSV_ID_FIELD]).filter(notUndefined)),
	];
	return getExistingArticlesByIds(shopId, allRowIdCellValues);
};

const getExistingArticlesFromCodeField = async (
	dataRows: DataRowObject[],
	shopId: string,
	opts: { previouslyFetchedArticles: InventoryItem[] },
) => {
	const allRowArticleCodeValues = [
		...new Set(dataRows.map((row) => row.article_code).filter(notUndefined)),
	].flatMap(articleCodesArrayFromString);
	const articleCodesMapFromPreviouslyFetchedArticles = createKeyMap(
		opts.previouslyFetchedArticles.flatMap((a) => a.identifiers ?? []),
	);
	const articleCodesToFetch = allRowArticleCodeValues.filter(
		(code) => !articleCodesMapFromPreviouslyFetchedArticles[code],
	);
	return getExistingArticlesByCodes(shopId, articleCodesToFetch);
};

const getExistingBulkArticlesFromSkuAndStoreFields = async (
	dataRows: DataRowObject[],
	shopId: string,
	opts: { locations: Location[]; previouslyFetchedArticles: InventoryItem[] },
) => {
	const rowToLocationIdSkuCode = uniqWith(
		dataRows
			.filter((row) => row.type !== 'single')
			.map((row) => {
				const locationId = getLocationIdByRowLocationName(row.store ?? null, opts.locations);
				const skuCode = row.sku_code;
				if (!!locationId && !!skuCode) {
					return { locationId, skuCode };
				}
				return undefined;
			})
			.filter(notUndefined),
		isEqual,
	);

	const bulkArticleLocationIdsBySkuCode = getLocationIdsBySkuCode(
		opts.previouslyFetchedArticles.filter((a): a is BulkInventoryItem => a.type === 'bulk'),
	);

	const rowToLocationIdSkuCodeItemsToFetch = rowToLocationIdSkuCode.filter(
		({ locationId, skuCode }) => !bulkArticleLocationIdsBySkuCode[skuCode]?.includes(locationId),
	);
	return getExistingBulkArticles(shopId, rowToLocationIdSkuCodeItemsToFetch);
};

export const getExistingInventoryData = async (
	dataRows: DataRowObject[],
	opts: { locations: Location[]; shopId: string },
): Promise<{ articles: InventoryItem[]; inventoryData: ExistingInventoryData }> => {
	const { locations, shopId } = opts;

	const existingArticlesFromIds = await getExistingArticlesFromIdField(dataRows, shopId);
	const existingArticlesFromCodes = await getExistingArticlesFromCodeField(dataRows, shopId, {
		previouslyFetchedArticles: existingArticlesFromIds,
	});
	const existingBulkArticlesFromSkuAndLocation = await getExistingBulkArticlesFromSkuAndStoreFields(
		dataRows,
		shopId,
		{
			locations,
			previouslyFetchedArticles: [...existingArticlesFromIds, ...existingArticlesFromCodes],
		},
	);
	const existingArticles = [
		...existingArticlesFromIds,
		...existingArticlesFromCodes,
		...existingBulkArticlesFromSkuAndLocation,
	];
	const existingArticleCodes = existingArticles.flatMap((a) => a.identifiers ?? []);
	const locationIdsByBulkSkuCode = getLocationIdsBySkuCode(
		existingArticles.filter((a): a is BulkInventoryItem => a.type === 'bulk'),
	);
	return {
		articles: existingArticles,
		inventoryData: { articleCodes: existingArticleCodes, locationIdsByBulkSkuCode },
	};
};

export const sanitizeDataRowObject = (
	row: DataRowObject,
	validCustomFieldIds: string[] | null,
): SanitizedDataRowObject => {
	const { article_code, status, allocations, type } = FixedArticleColumns;
	const sanitizedObject = Object.entries(row).reduce((tot, [key, _value]) => {
		let value = _value;
		if (!key) return tot;
		if (!!FixedArticleColumns[key] && !allowedImportFields.includes(key as any)) return tot;
		if (!FixedArticleColumns[key] && !!validCustomFieldIds && !validCustomFieldIds.includes(key)) {
			return tot;
		}
		if (key === article_code) {
			value = value.toLocaleUpperCase();
		}
		if (key === status) {
			value = value.toLocaleUpperCase();
		}
		if (key === allocations) {
			value = value.toLocaleLowerCase();
		}
		if (key === type) {
			value = value.toLocaleLowerCase();
		}
		return {
			...tot,
			[key]: value,
		};
	}, {} as SanitizedDataRowObject);
	return sanitizedObject;
};

export const articleCodesArrayFromString = (articleIdentifier: string): string[] =>
	articleIdentifier
		.toLocaleUpperCase()
		.split(',')
		.map((id) => id.trim());

export const articleCodesStringFromArray = (articleIdentifiers: string[] | undefined): string =>
	(articleIdentifiers ?? []).join(',');

export function getValidValuesText(t: TFunction, field: 'store', userLocations: Location[]): string;
export function getValidValuesText(
	t: TFunction,
	field: Exclude<AllowedImportField, 'store'>,
): string;
export function getValidValuesText(
	t: TFunction,
	field: AllowedImportField,
	userLocations?: Location[],
): string | undefined {
	const validValuesText = t('inventory.import.validValues', 'Valid values');
	if (field === 'store') {
		return `${validValuesText}: ${userLocations?.map((l) => l.name).join(', ')}`;
	}
	const validValuesByField: Record<Exclude<AllowedImportField, 'store'>, string | undefined> = {
		id: undefined,
		article_code: undefined,
		sku_code: undefined,
		sku_name: undefined,
		status: inventoryStatusList.join(', '),
		allocations: inventoryAllocationList.join(', '),
		quantity: undefined,
		type: ['single', 'bulk'].join(', '),
		sole_length: undefined,
		din_variation: undefined,
	};
	if (!validValuesByField[field]) return '';
	return `${validValuesText}: ${validValuesByField[field]}`;
}
